import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Modal: pick one of my upcoming shifts and (optionally) a target coworker.
export function OfferShiftModal({ currentUser, onClose, onCreated }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [shifts, setShifts] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [shiftId, setShiftId] = useState('');
  const [targetId, setTargetId] = useState('');
  // Docket #29: the shift being asked for IN RETURN. Liv and Riley could not
  // trade an open for a mid on the same day — whoever accepted first was
  // "double-booked" by the shift they were in the act of giving away, so
  // neither could go first. Naming both sides is what breaks that deadlock.
  const [theirShifts, setTheirShifts] = useState([]);
  const [counterId, setCounterId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api('GET', '/api/me/shifts/upcoming', null, headers)
      .then(r => setShifts(r.shifts || []))
      .catch(() => setShifts([]));
    api('GET', '/api/chat/recipients', null, headers)
      .then(r => setRecipients((r.users || []).filter(u => u.id !== currentUser.id)))
      .catch(() => setRecipients([]));
  }, []);

  // Their upcoming shifts, so a trade can name one. Cleared when the target
  // changes, or the picker would keep offering the previous person's shifts.
  useEffect(() => {
    setCounterId('');
    if (!targetId) { setTheirShifts([]); return; }
    api('GET', `/api/swaps/tradeable/${targetId}`, null, headers)
      .then(r => setTheirShifts(r.shifts || []))
      .catch(() => setTheirShifts([]));
  }, [targetId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function submit(e) {
    e.preventDefault();
    if (!shiftId) return;
    setSubmitting(true);
    setErr('');
    try {
      await api('POST', `/api/me/shifts/${shiftId}/swap`,
        { target_user_id: targetId ? Number(targetId) : null,
          counter_shift_id: counterId ? Number(counterId) : null,
          reason: reason.trim() || null },
        headers);
      onCreated();
    } catch (e2) { setErr(e2.message || String(e2)); }
    finally { setSubmitting(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
      <form onSubmit={submit}
        style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 480, maxHeight: '85vh',
                  display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: 16, borderBottom: '1px solid #e0e0e0' }}>
          {/* CJ 2026-08-23: "Avery and Abigail tried but couldn't do it for the
              same day." The server logs show they never reached the API at all —
              no swap request was ever submitted. Trading WAS possible by then;
              it was buried in an optional dropdown that only appears after you
              pick a person, and nothing on the screen used the word "trade".
              A capability nobody can find is not a capability. */}
          <div style={{ fontSize: 16, fontWeight: 700 }}>Offer or trade a shift</div>
          <div style={{ fontSize: 12, color: '#757575' }}>Give a shift away, or trade it for one of theirs.</div>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>
            Shift
            <select value={shiftId} onChange={(e) => setShiftId(e.target.value)} required
              style={{ width: '100%', marginTop: 4, padding: 8, fontFamily: 'inherit', fontSize: 14, border: '1px solid #d0d4d9', borderRadius: 6 }}>
              <option value="">— choose a shift —</option>
              {shifts.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.shift_date}  {(s.start_time || '').slice(0, 5)}–{(s.end_time || '').slice(0, 5)}
                  {s.location_name ? ` · ${s.location_name}` : ''}
                </option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 12, fontWeight: 600 }}>
            Offer to
            <span style={{ display: 'block', fontWeight: 400, color: '#757575', marginTop: 2 }}>
              Pick a person if you want to <b>trade</b> — swapping shifts needs a
              named coworker, not an open offer.
            </span>
            <select value={targetId} onChange={(e) => setTargetId(e.target.value)}
              style={{ width: '100%', marginTop: 4, padding: 8, fontFamily: 'inherit', fontSize: 14, border: '1px solid #d0d4d9', borderRadius: 6 }}>
              <option value="">Anyone at this location — give away, no trade</option>
              {recipients.map((u) => (
                <option key={u.id} value={u.id}>{u.name} · {u.role}</option>
              ))}
            </select>
          </label>
          {/* Only for a 1:1 offer — a trade with "whoever sees this first" is
              not a trade, and the server refuses one. */}
          {targetId && (
            <label style={{ fontSize: 12, fontWeight: 600 }}>
              Take one of theirs in return (optional)
              <select value={counterId} onChange={(e) => setCounterId(e.target.value)}
                style={{ width: '100%', marginTop: 4, padding: 8, fontFamily: 'inherit', fontSize: 14, border: '1px solid #d0d4d9', borderRadius: 6 }}>
                <option value="">Just give my shift away</option>
                {theirShifts.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.shift_date}  {(s.start_time || '').slice(0, 5)}–{(s.end_time || '').slice(0, 5)}
                    {s.location_name ? ` · ${s.location_name}` : ''}
                  </option>
                ))}
              </select>
              <span style={{ display: 'block', fontWeight: 400, color: '#757575', marginTop: 3 }}>
                {theirShifts.length === 0
                  ? 'They have no upcoming shifts to trade.'
                  : 'Pick one to trade — you both change shifts at the same moment, once they accept.'}
              </span>
            </label>
          )}
          <label style={{ fontSize: 12, fontWeight: 600 }}>
            Reason (optional)
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. dentist appointment"
              style={{ width: '100%', marginTop: 4, padding: 8, fontFamily: 'inherit', fontSize: 14, border: '1px solid #d0d4d9', borderRadius: 6, resize: 'vertical' }} />
          </label>
          {err && <div style={{ fontSize: 12, color: '#c62828' }}>{err}</div>}
        </div>
        <div style={{ padding: 12, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={submitting || !shiftId} className="btn-primary">
            {submitting ? 'Submitting…' : 'Send offer'}
          </button>
        </div>
      </form>
    </div>
  );
}
